import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Briefcase, Clock, Heart, Sparkles } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { ApplicationForm } from "@/components/ApplicationForm";

export default function Karriere() {
  const [meta, setMeta] = useState({ employment_types: [], areas: [], branches: [], intro: "" });
  const [jobs, setJobs] = useState([]);
  const [filters, setFilters] = useState({ branch_id: "", employment_type: "", area: "" });
  const [loading, setLoading] = useState(true);
  const [showInitiative, setShowInitiative] = useState(false);

  useEffect(() => { api.get("/careers/meta").then((r) => setMeta(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
    api.get(`/careers/jobs?${params.toString()}`).then((r) => { setJobs(r.data); setLoading(false); });
  }, [filters]);

  const sel = "px-4 py-2.5 rounded-xl border border-gray-200 bg-white font-medium text-sm";
  return (
    <div>
      <Seo title="Karriere bei HELIUM Catering | Jobs in Helios-Kliniken NRW" description="Werde Teil des HELIUM-Teams. Offene Stellen in unseren Cafeterien in Schwelm, Wuppertal und Duisburg – Küche, Service, Leitung & mehr." />

      {/* Hero */}
      <section className="bg-[#1F2937] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <span className="inline-flex items-center gap-2 bg-[#D62B3A] text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
            Karriere bei HELIUM
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">
            Arbeite im <span className="text-[#D62B3A]">Herz</span> des Krankenhauses
          </h1>
          <p className="mt-5 text-lg text-gray-300 max-w-2xl">{meta.intro || "Werde Teil unseres Teams in den Helios-Kliniken in NRW."}</p>
          <div className="mt-8 flex flex-wrap gap-6">
            <div className="flex items-center gap-2 text-gray-200"><Heart className="w-5 h-5 text-[#D62B3A]" /> Herzliches Team</div>
            <div className="flex items-center gap-2 text-gray-200"><Clock className="w-5 h-5 text-[#D62B3A]" /> Geregelte Arbeitszeiten</div>
            <div className="flex items-center gap-2 text-gray-200"><Sparkles className="w-5 h-5 text-[#D62B3A]" /> Entwicklungsmöglichkeiten</div>
          </div>
        </div>
      </section>

      {/* Filters + list */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="flex flex-wrap gap-3 mb-8" data-testid="career-filters">
          <select value={filters.branch_id} onChange={(e) => setFilters((f) => ({ ...f, branch_id: e.target.value }))} className={sel} data-testid="filter-branch">
            <option value="">Alle Standorte</option>
            {meta.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <select value={filters.employment_type} onChange={(e) => setFilters((f) => ({ ...f, employment_type: e.target.value }))} className={sel} data-testid="filter-employment">
            <option value="">Alle Beschäftigungsarten</option>
            {meta.employment_types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filters.area} onChange={(e) => setFilters((f) => ({ ...f, area: e.target.value }))} className={sel} data-testid="filter-area">
            <option value="">Alle Bereiche</option>
            {meta.areas.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>

        {loading ? (
          <p className="text-gray-400">Laden…</p>
        ) : jobs.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="job-list">
            {jobs.map((j) => (
              <Link key={j.id} to={`/karriere/${j.slug}`} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-[#D62B3A]/30 transition-all p-6 flex flex-col group" data-testid={`job-card-${j.slug}`}>
                <div className="flex items-center gap-2 text-xs font-semibold text-[#D62B3A] uppercase tracking-wide">
                  <Briefcase className="w-4 h-4" /> {j.area}
                </div>
                <h3 className="mt-2 text-xl font-bold group-hover:text-[#D62B3A] transition-colors">{j.title}</h3>
                <div className="mt-2 flex flex-wrap gap-3 text-sm text-gray-500">
                  <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {j.branch_name}</span>
                  <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {j.employment_type}</span>
                </div>
                <p className="mt-3 text-sm text-gray-600 flex-1">{j.short_description}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#D62B3A]">Details ansehen <ArrowRight className="w-4 h-4" /></span>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-[#F9F8F6] rounded-2xl" data-testid="jobs-empty">
            <Briefcase className="w-12 h-12 text-gray-300 mx-auto" />
            <h3 className="mt-4 text-xl font-bold">Aktuell keine offenen Stellen</h3>
            <p className="mt-2 text-gray-600 max-w-md mx-auto">Zurzeit sind für die gewählten Filter keine Stellen ausgeschrieben. Wir freuen uns aber jederzeit über deine Initiativbewerbung!</p>
          </div>
        )}

        {/* Initiativbewerbung – always visible */}
        <div className="mt-12 bg-gradient-to-br from-[#D62B3A] to-[#b8232f] rounded-2xl p-8 lg:p-10 text-white" data-testid="initiative-block">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-bold">Keine passende Stelle dabei?</h3>
              <p className="mt-2 text-white/90 max-w-xl">Sende uns deine Initiativbewerbung – wir freuen uns darauf, dich kennenzulernen und melden uns, sobald etwas Passendes frei wird.</p>
            </div>
            <button onClick={() => setShowInitiative((v) => !v)} className="px-6 py-3.5 rounded-xl bg-white text-[#D62B3A] font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap" data-testid="initiative-toggle">
              {showInitiative ? "Formular schließen" : "Initiativbewerbung starten"}
            </button>
          </div>
          {showInitiative && (
            <div className="mt-8 bg-white rounded-2xl p-6 lg:p-8 text-gray-900">
              <ApplicationForm job={null} branches={meta.branches} />
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
